import { parseLocalDate } from '@/shared/i18n/dateUtils';
export type Weekday =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const ALL_WEEKDAYS: readonly Weekday[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const OFFERED_WEEKDAYS: readonly Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

export function weekdayOf(date: string): Weekday {
  return ALL_WEEKDAYS[parseLocalDate(date).getDay()];
}

const REFERENCE_SUNDAY = Date.UTC(2024, 0, 7);
const MILLISECONDS_PER_DAY = 86_400_000;

export function weekdayName(
  weekday: Weekday,
  locale: string,
  style: 'long' | 'short' | 'narrow'
): string {
  const instant = REFERENCE_SUNDAY + ALL_WEEKDAYS.indexOf(weekday) * MILLISECONDS_PER_DAY;

  return new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(instant);
}

export function canonicalWeekdays(weekdays: readonly Weekday[]): Weekday[] {
  return [...new Set(weekdays)].toSorted(
    (one, other) => ALL_WEEKDAYS.indexOf(one) - ALL_WEEKDAYS.indexOf(other)
  );
}

export function isOffered(weekday: Weekday): boolean {
  return OFFERED_WEEKDAYS.includes(weekday);
}

interface PartitionedWeekdays {
  offered: Weekday[];
  passthrough: Weekday[];
}

export function partitionOffered(weekdays: readonly Weekday[]): PartitionedWeekdays {
  return {
    offered: weekdays.filter((weekday) => isOffered(weekday)),
    passthrough: weekdays.filter((weekday) => !isOffered(weekday)),
  };
}

export function normalizeWeekdays(
  offered: readonly Weekday[],
  passthrough: readonly Weekday[]
): Weekday[] {
  const everyOfferedDay = OFFERED_WEEKDAYS.every((weekday) => offered.includes(weekday));
  if (everyOfferedDay && passthrough.length === 0) return [];

  return canonicalWeekdays([...offered, ...passthrough]);
}
